import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  ArrowLeft, Bookmark, BookmarkCheck, Plus, ExternalLink, 
  Package, MessageSquare, ChevronLeft, ChevronRight, X, Check, ChevronsUpDown, 
  MapPin, Home, Scale, FileText, Sparkles, Eye, Layers, Droplets, Sun, Move, Camera
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import AddToCollectionButton from '../components/AddToCollectionButton';
import WhereToBuySection from '../components/WhereToBuySection';
import { useAnalytics } from '../hooks/useAnalytics';
import RequestSlabsModal from '../components/materials/RequestSlabsModal';

// US Cities for board creation
const US_CITIES = [
  "New York, NY", "Los Angeles, CA", "Chicago, IL", "Houston, TX", "Phoenix, AZ",
  "Philadelphia, PA", "San Antonio, TX", "San Diego, CA", "Dallas, TX", "San Jose, CA",
  "Austin, TX", "Jacksonville, FL", "Fort Worth, TX", "Columbus, OH", "Charlotte, NC",
  "San Francisco, CA", "Indianapolis, IN", "Seattle, WA", "Denver, CO", "Boston, MA",
  "Nashville, TN", "Detroit, MI", "Portland, OR", "Las Vegas, NV", "Miami, FL",
  "Atlanta, GA", "Scottsdale, AZ", "Beverly Hills, CA", "Palm Beach, FL", "Aspen, CO",
  "Greenwich, CT", "Hamptons, NY", "Malibu, CA", "Newport Beach, CA", "Naples, FL"
];

// ========== VISUAL CHARACTERISTICS TAG ==========
const VisualTag = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 px-3 py-2 bg-stone-100 text-sm">
    <Icon className="w-4 h-4 text-stone-500" />
    <span className="text-stone-600">{label}:</span>
    <span className="text-stone-900 font-medium">{value}</span>
  </div>
);

// ========== SIMILAR MATERIAL CARD ==========
const SimilarMaterialCard = ({ material }) => {
  const navigate = useNavigate();
  
  return (
    <div 
      onClick={() => navigate(`/materials/${material.material_id}`)}
      className="group cursor-pointer"
      data-testid={`similar-material-${material.material_id}`}
    >
      <div className="aspect-square bg-stone-200 overflow-hidden mb-3">
        <img 
          src={material.images?.[0] || 'https://images.unsplash.com/photo-1618220252344-8ec99ec624b1?w=400&q=80'} 
          alt={material.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
      </div>
      <p className="text-sm font-medium text-stone-900 truncate">{material.name}</p>
      <p className="text-xs text-stone-500 capitalize">{material.stone_type}</p>
    </div>
  );
};

// ========== IMAGE GALLERY COMPONENT ==========
const ImageGallery = ({ images, materialName, onImageClick }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  if (!images || images.length === 0) return null;
  
  return (
    <div className="space-y-4">
      {/* Main Image */}
      <div 
        className="relative aspect-[4/3] md:aspect-[16/10] bg-stone-200 overflow-hidden cursor-pointer"
        onClick={() => onImageClick(activeIndex)}
        data-testid="main-gallery-image"
      >
        <img 
          src={images[activeIndex]} 
          alt={materialName}
          className="w-full h-full object-cover"
        />
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); setActiveIndex(prev => prev > 0 ? prev - 1 : images.length - 1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white transition-colors"
              data-testid="gallery-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setActiveIndex(prev => prev < images.length - 1 ? prev + 1 : 0); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/90 hover:bg-white transition-colors"
              data-testid="gallery-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
        <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/60 text-white text-sm">
          {activeIndex + 1} / {images.length}
        </div>
      </div>
      
      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setActiveIndex(idx)}
              className={cn(
                "aspect-square overflow-hidden transition-all",
                activeIndex === idx ? "ring-2 ring-stone-900" : "opacity-60 hover:opacity-100"
              )}
              data-testid={`gallery-thumb-${idx}`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ========== MAIN COMPONENT ==========
const MaterialDetail = () => {
  const { materialId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Use ref to track current fetch to avoid stale closures
  const fetchingRef = React.useRef(null);
  
  const [material, setMaterial] = useState(null);
  const [boards, setBoards] = useState([]);
  const [similarMaterials, setSimilarMaterials] = useState([]);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [showAddToBoard, setShowAddToBoard] = useState(false);
  const [showCreateBoard, setShowCreateBoard] = useState(false);
  const [showAdvisorDialog, setShowAdvisorDialog] = useState(false);
  const [advisorMessage, setAdvisorMessage] = useState('');
  const [sendingAdvisorRequest, setSendingAdvisorRequest] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [cityOpen, setCityOpen] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const { trackMaterialDetailView, trackMaterialSaved, trackMaterialAddedToBoard, trackSpecSheetDownloaded } = useAnalytics();
  
  const [newBoardForm, setNewBoardForm] = useState({
    name: '',
    project_name: '',
    city: '',
    notes: ''
  });

  // Filter cities
  const filteredCities = useMemo(() => {
    if (!citySearch) return US_CITIES.slice(0, 15);
    return US_CITIES.filter(city => 
      city.toLowerCase().includes(citySearch.toLowerCase())
    ).slice(0, 15);
  }, [citySearch]);

  // Check which boards already have this material
  const boardsWithMaterial = useMemo(() => {
    return new Set(boards.filter(b => 
      b.items?.some(item => item.material_id === materialId)
    ).map(b => b.board_id));
  }, [boards, materialId]);

  // All images combined
  const allImages = useMemo(() => {
    if (!material) return [];
    return [...(material.images || []), ...(material.texture_images || []), ...(material.render_images || [])];
  }, [material]);

  useEffect(() => {
    // Skip if already fetching or loaded for this materialId
    if (fetchingRef.current === materialId) {
      return;
    }
    
    fetchingRef.current = materialId;
    setLoading(true);
    
    const fetchData = async () => {
      try {
        // Fetch material first (critical)
        const materialRes = await axios.get(`${API}/materials/${materialId}`);
        
        // Only update state if we're still fetching the same material
        if (fetchingRef.current === materialId) {
          setMaterial(materialRes.data);
        
          // Fetch secondary data in parallel (non-critical)
          const [boardsRes, savedRes, similarRes] = await Promise.all([
            axios.get(`${API}/boards`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${API}/saved-materials`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${API}/materials?limit=6`).catch(() => ({ data: [] }))
          ]);
          
          if (fetchingRef.current === materialId) {
            setBoards(boardsRes.data || []);
            setIsSaved((savedRes.data || []).some(m => m.material_id === materialId));
            
            // Track material detail view
            trackMaterialDetailView(materialId, { stone_type: materialRes.data?.stone_type });
            
            // Filter out current material and get similar by type
            const similar = (similarRes.data || [])
              .filter(m => m.material_id !== materialId)
              .slice(0, 6);
            setSimilarMaterials(similar);
          }
        }
        
        // Try to fetch related projects
        try {
          const projectsRes = await axios.get(`${API}/project-inspiration?limit=3`);
          setRelatedProjects(projectsRes.data);
        } catch {
          setRelatedProjects([]);
        }
      } catch (error) {
        console.error('Failed to fetch material:', error);
        if (fetchingRef.current === materialId) {
          toast.error('Material not found');
          navigate('/materials');
        }
      } finally {
        if (fetchingRef.current === materialId) {
          setLoading(false);
        }
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialId]);

  const toggleSave = async () => {
    if (!user) {
      toast.error('Please log in to save materials');
      navigate('/login', { state: { from: `/materials/${materialId}` } });
      return;
    }
    
    try {
      if (isSaved) {
        await axios.delete(`${API}/saved-materials/${materialId}`, { withCredentials: true });
        setIsSaved(false);
        toast.success('Material removed from saved');
      } else {
        await axios.post(`${API}/saved-materials/${materialId}`, {}, { withCredentials: true });
        setIsSaved(true);
        trackMaterialSaved(materialId);
        toast.success('Material saved');
      }
    } catch (error) {
      toast.error('Failed to update saved materials');
    }
  };

  const sendAdvisorRequest = async () => {
    if (!user) {
      toast.error('Please log in to contact an advisor');
      navigate('/login', { state: { from: `/materials/${materialId}` } });
      return;
    }
    
    if (!advisorMessage.trim()) {
      toast.error('Please enter your question');
      return;
    }
    
    setSendingAdvisorRequest(true);
    try {
      await axios.post(`${API}/advisor/requests`, {
        material_id: materialId,
        material_name: material?.name,
        message: advisorMessage,
        request_type: 'material_inquiry'
      }, { withCredentials: true });
      
      toast.success('Request sent! An advisor will contact you soon.');
      setShowAdvisorDialog(false);
      setAdvisorMessage('');
    } catch (error) {
      toast.error('Failed to send request. Please try again.');
    } finally {
      setSendingAdvisorRequest(false);
    }
  };

  const addToBoard = async (boardId, environmentId = null) => {
    try {
      await axios.post(`${API}/boards/${boardId}/items`, {
        material_id: materialId,
        environment_id: environmentId || undefined
      }, { withCredentials: true });
      trackMaterialAddedToBoard(materialId, boardId);
      toast.success('Added to project');
      setBoards(boards.map(b => {
        if (b.board_id === boardId) {
          return { ...b, items: [...(b.items || []), { material_id: materialId, environment_id: environmentId }] };
        }
        return b;
      }));
      setShowAddToBoard(false);
      setSelectedBoard(null);
    } catch (error) {
      if (error.response?.data?.detail?.includes('already exists')) {
        toast.error('Material is already in this project/room');
      } else {
        toast.error('Failed to add to project');
      }
    }
  };

  const selectBoardForAdd = (board) => {
    if (board.environments?.length > 0) {
      setSelectedBoard(board);
    } else {
      addToBoard(board.board_id);
    }
  };

  const createBoardAndAdd = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/boards`, newBoardForm, { withCredentials: true });
      const newBoard = response.data;
      
      await axios.post(`${API}/boards/${newBoard.board_id}/items`, {
        material_id: materialId
      }, { withCredentials: true });
      
      toast.success('Board created and material added!');
      setBoards([newBoard, ...boards]);
      setShowCreateBoard(false);
      setShowAddToBoard(false);
      setNewBoardForm({ name: '', project_name: '', city: '', notes: '' });
    } catch (error) {
      toast.error('Failed to create board');
    }
  };

  const openGallery = (index) => {
    setGalleryIndex(index);
    setShowGallery(true);
  };

  // Extract visual profile from material (mock data for now)
  const visualProfile = {
    color_family: material?.color || 'White',
    pattern_type: 'Veined',
    contrast_level: 'Medium',
    visual_movement: 'Moderate'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-stone-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!material) return null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="bg-stone-50">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Breadcrumb */}
            <Breadcrumb 
              items={[{ label: 'Materials', href: '/materials' }]}
              currentPage={material.name}
            />
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Left: Main Image */}
              <div className="aspect-square bg-stone-200 overflow-hidden">
                <img 
                  src={allImages[0] || 'https://images.unsplash.com/photo-1618220252344-8ec99ec624b1?w=800&q=80'} 
                  alt={material.name}
                  className="w-full h-full object-cover cursor-pointer"
                  onClick={() => openGallery(0)}
                  data-testid="hero-image"
                />
              </div>
              
              {/* Right: Material Info */}
              <div className="flex flex-col justify-center">
                <p className="text-xs tracking-widest uppercase text-stone-500 mb-2">{material.stone_type}</p>
                <h1 className="text-4xl md:text-5xl font-medium text-stone-900 mb-4" data-testid="material-name">
                  {material.name}
                </h1>
                
                {/* Visual Tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  <VisualTag icon={Droplets} label="Color" value={visualProfile.color_family} />
                  <VisualTag icon={Layers} label="Pattern" value={visualProfile.pattern_type} />
                  <VisualTag icon={Sun} label="Contrast" value={visualProfile.contrast_level} />
                  <VisualTag icon={Move} label="Movement" value={visualProfile.visual_movement} />
                </div>
                
                {/* Primary Actions */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {/* Richiedi Lastre - CTA Principale */}
                  <Button 
                    onClick={() => setShowRequestModal(true)}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="request-slabs-btn"
                  >
                    <Package className="w-4 h-4 mr-2" /> Richiedi Lastre
                  </Button>
                  
                  <Button 
                    onClick={() => setShowRequestModal(true)}
                    variant="outline" 
                    className="border-amber-600 text-amber-700 hover:bg-amber-50"
                    data-testid="request-photos-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" /> Foto Lastre Reali
                  </Button>
                  
                  <Button 
                    onClick={toggleSave}
                    variant="outline" 
                    className="border-stone-300 hover:border-stone-500"
                    data-testid="save-material-btn"
                  >
                    {isSaved ? <BookmarkCheck className="w-4 h-4 mr-2" /> : <Bookmark className="w-4 h-4 mr-2" />}
                    {isSaved ? 'Saved' : 'Save to board'}
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="border-stone-300 hover:border-stone-500"
                    onClick={() => navigate(`/compare?ids=${material.material_id}`)}
                    data-testid="compare-btn"
                  >
                    <Scale className="w-4 h-4 mr-2" /> Compare
                  </Button>
                  
                  {user && (
                    <AddToCollectionButton 
                      materialId={material.material_id} 
                      materialName={material.name}
                      className="px-4 py-2 border border-stone-300 hover:border-stone-500 bg-white hover:bg-stone-50 text-sm"
                    />
                  )}
                  
                  <Dialog open={showAdvisorDialog} onOpenChange={setShowAdvisorDialog}>
                    <DialogTrigger asChild>
                      <Button 
                        className="bg-stone-900 hover:bg-stone-800 text-white"
                        data-testid="ask-advisor-btn"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" /> Ask Material Advisor
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle className="text-xl font-medium">Ask a Material Advisor</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <p className="text-sm text-stone-600">
                          Have questions about <strong>{material.name}</strong>? Our material experts are here to help.
                        </p>
                        <div>
                          <Label className="mb-2 block text-sm">Your Question</Label>
                          <Textarea
                            value={advisorMessage}
                            onChange={(e) => setAdvisorMessage(e.target.value)}
                            placeholder="e.g., What finishes are available? Is this suitable for outdoor use? What's the lead time?"
                            className="min-h-[120px]"
                          />
                        </div>
                        <Button
                          onClick={sendAdvisorRequest}
                          disabled={sendingAdvisorRequest || !advisorMessage.trim()}
                          className="w-full bg-stone-900 hover:bg-stone-800"
                        >
                          {sendingAdvisorRequest ? 'Sending...' : 'Send Request'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                
                {/* Secondary Actions */}
                <div className="flex gap-3">
                  {user ? (
                    <Dialog open={showAddToBoard} onOpenChange={(open) => {
                      setShowAddToBoard(open);
                      if (!open) setSelectedBoard(null);
                    }}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="flex-1 border-stone-300" data-testid="add-to-board-btn">
                          <Plus className="w-4 h-4 mr-2" /> Add to Project
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle className="text-xl font-medium">
                            {selectedBoard ? `Add to ${selectedBoard.name}` : 'Add to Project'}
                          </DialogTitle>
                        </DialogHeader>
                        
                        {selectedBoard ? (
                          <div className="space-y-3 mt-4">
                            <button
                              onClick={() => setSelectedBoard(null)}
                              className="flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900"
                            >
                              <ArrowLeft className="w-4 h-4" /> Back
                            </button>
                            
                            <button
                              onClick={() => addToBoard(selectedBoard.board_id, null)}
                              className="w-full p-4 text-left bg-stone-50 hover:bg-stone-100 transition-colors"
                            >
                              <p className="font-medium">General</p>
                              <p className="text-sm text-stone-500">Not assigned to a room</p>
                            </button>
                            
                            {selectedBoard.environments?.map(env => (
                              <button
                                key={env.environment_id}
                                onClick={() => addToBoard(selectedBoard.board_id, env.environment_id)}
                                className="w-full p-4 text-left bg-stone-50 hover:bg-stone-100 transition-colors flex items-center gap-3"
                              >
                                <Home className="w-5 h-5 text-stone-400" />
                                <div>
                                  <p className="font-medium">{env.name}</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-3 mt-4">
                            <button
                              onClick={() => setShowCreateBoard(true)}
                              className="w-full p-4 text-left border-2 border-dashed border-stone-200 hover:border-stone-400 transition-colors flex items-center gap-3"
                            >
                              <Plus className="w-5 h-5 text-stone-400" />
                              <div>
                                <p className="font-medium">Create New Project</p>
                                <p className="text-sm text-stone-500">Start a new design project</p>
                              </div>
                            </button>
                            
                            {boards.length > 0 && (
                              <div className="pt-2 border-t">
                                <p className="text-sm text-stone-500 mb-3">Or add to existing project</p>
                                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                  {boards.map(board => {
                                    const alreadyInBoard = boardsWithMaterial.has(board.board_id);
                                    return (
                                      <button
                                        key={board.board_id}
                                        onClick={() => !alreadyInBoard && selectBoardForAdd(board)}
                                        disabled={alreadyInBoard}
                                        className={cn(
                                          "w-full p-4 text-left transition-colors flex items-center justify-between",
                                          alreadyInBoard 
                                            ? "bg-stone-50 opacity-60 cursor-not-allowed" 
                                            : "bg-stone-50 hover:bg-stone-100"
                                        )}
                                      >
                                        <div>
                                          <p className="font-medium">{board.name}</p>
                                          {board.city && (
                                            <p className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                                              <MapPin className="w-3 h-3" /> {board.city}
                                            </p>
                                          )}
                                        </div>
                                        {alreadyInBoard && (
                                          <span className="text-xs bg-stone-200 px-2 py-1 flex items-center gap-1">
                                            <Check className="w-3 h-3" /> Added
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  ) : (
                    <Button 
                      variant="outline" 
                      className="flex-1 border-stone-300"
                      onClick={() => navigate('/login', { state: { from: `/materials/${materialId}` } })}
                    >
                      <Plus className="w-4 h-4 mr-2" /> Sign in to save
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline" 
                    className="flex-1 border-stone-300"
                    onClick={() => navigate(`/materials/${material.material_id}/specification`)}
                    data-testid="generate-spec-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" /> Specification
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Image Gallery Section */}
        {allImages.length > 1 && (
          <section className="py-12 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-2xl font-medium text-stone-900 mb-6">Gallery</h2>
              <ImageGallery 
                images={allImages} 
                materialName={material.name}
                onImageClick={openGallery}
              />
            </div>
          </section>
        )}
        
        {/* Material Overview */}
        <section className="py-12 bg-stone-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Description */}
              <div>
                <h2 className="text-2xl font-medium text-stone-900 mb-4">Overview</h2>
                <p className="text-stone-600 leading-relaxed mb-6" data-testid="material-description">
                  {material.description}
                </p>
                
                {/* Recommended Applications */}
                {material.applications?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-stone-900 mb-3 uppercase tracking-wider">Recommended Applications</h3>
                    <div className="flex flex-wrap gap-2">
                      {material.applications.map(app => (
                        <span key={app} className="px-4 py-2 bg-white border border-stone-200 text-sm capitalize">
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Visual & Technical Characteristics */}
              <div className="space-y-8">
                {/* Visual Characteristics */}
                <div>
                  <h3 className="text-sm font-medium text-stone-900 mb-4 uppercase tracking-wider">Visual Characteristics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-white border border-stone-200">
                      <p className="text-xs text-stone-500 mb-1">Color Family</p>
                      <p className="font-medium text-stone-900">{visualProfile.color_family}</p>
                    </div>
                    <div className="p-4 bg-white border border-stone-200">
                      <p className="text-xs text-stone-500 mb-1">Pattern Type</p>
                      <p className="font-medium text-stone-900">{visualProfile.pattern_type}</p>
                    </div>
                    <div className="p-4 bg-white border border-stone-200">
                      <p className="text-xs text-stone-500 mb-1">Contrast Level</p>
                      <p className="font-medium text-stone-900">{visualProfile.contrast_level}</p>
                    </div>
                    <div className="p-4 bg-white border border-stone-200">
                      <p className="text-xs text-stone-500 mb-1">Visual Movement</p>
                      <p className="font-medium text-stone-900">{visualProfile.visual_movement}</p>
                    </div>
                  </div>
                </div>
                
                {/* Technical Characteristics */}
                <div>
                  <h3 className="text-sm font-medium text-stone-900 mb-4 uppercase tracking-wider">Technical Characteristics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {material.finishes?.length > 0 && (
                      <div className="p-4 bg-white border border-stone-200">
                        <p className="text-xs text-stone-500 mb-1">Available Finishes</p>
                        <p className="font-medium text-stone-900">{material.finishes.join(', ')}</p>
                      </div>
                    )}
                    {material.origin && (
                      <div className="p-4 bg-white border border-stone-200">
                        <p className="text-xs text-stone-500 mb-1">Origin</p>
                        <p className="font-medium text-stone-900">{material.origin}</p>
                      </div>
                    )}
                    {material.applications?.length > 0 && (
                      <div className="p-4 bg-white border border-stone-200">
                        <p className="text-xs text-stone-500 mb-1">Usage</p>
                        <p className="font-medium text-stone-900 capitalize">
                          {material.applications.includes('exterior') ? 'Interior & Exterior' : 'Interior'}
                        </p>
                      </div>
                    )}
                    <div className="p-4 bg-white border border-stone-200">
                      <p className="text-xs text-stone-500 mb-1">Maintenance</p>
                      <p className="font-medium text-stone-900">Seal periodically</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Where to Buy Section */}
        <WhereToBuySection 
          materialId={material.material_id || materialId}
          materialName={material.name}
          user={user}
        />
        
        {/* Similar Materials */}
        {similarMaterials.length > 0 && (
          <section className="py-12 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h2 className="text-2xl font-medium text-stone-900">Materials with a similar look</h2>
                  <p className="text-stone-500 mt-1">Based on visual characteristics</p>
                </div>
                <Link to={`/materials/similar-to/${material.material_id}`} className="text-sm text-stone-600 hover:text-stone-900 flex items-center gap-1">
                  View all similar <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
                {similarMaterials.map((mat) => (
                  <SimilarMaterialCard key={mat.material_id} material={mat} />
                ))}
              </div>
            </div>
          </section>
        )}
        
        {/* Compare Alternatives Table */}
        <section className="py-12 bg-stone-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-2xl font-medium text-stone-900 mb-6">Compare Alternatives</h2>
            <div className="overflow-x-auto">
              <table className="w-full bg-white border border-stone-200">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Material</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Category</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Pattern</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Contrast</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Applications</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <td className="p-4 font-medium text-stone-900">{material.name}</td>
                    <td className="p-4 text-stone-600 capitalize">{material.stone_type}</td>
                    <td className="p-4 text-stone-600">{visualProfile.pattern_type}</td>
                    <td className="p-4 text-stone-600">{visualProfile.contrast_level}</td>
                    <td className="p-4 text-stone-600">{material.applications?.slice(0, 2).join(', ') || '-'}</td>
                  </tr>
                  {similarMaterials.slice(0, 3).map((mat) => (
                    <tr key={mat.material_id} className="border-b border-stone-100 hover:bg-stone-50 cursor-pointer" onClick={() => navigate(`/materials/${mat.material_id}`)}>
                      <td className="p-4 font-medium text-stone-900">{mat.name}</td>
                      <td className="p-4 text-stone-600 capitalize">{mat.stone_type}</td>
                      <td className="p-4 text-stone-600">Veined</td>
                      <td className="p-4 text-stone-600">Medium</td>
                      <td className="p-4 text-stone-600">{mat.applications?.slice(0, 2).join(', ') || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        
        {/* Projects Using This Material */}
        {relatedProjects.length > 0 && (
          <section className="py-12 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-2xl font-medium text-stone-900 mb-6">Projects using this material</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedProjects.map((project) => (
                  <Link key={project.project_id} to={`/project-inspiration/${project.project_id}`} className="group">
                    <div className="aspect-[4/3] bg-stone-200 overflow-hidden mb-3">
                      <img 
                        src={project.images?.[0] || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80'} 
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <p className="font-medium text-stone-900">{project.title}</p>
                    <p className="text-sm text-stone-500 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {project.location}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
        
        {/* Material Advisor CTA */}
        <section className="py-16 bg-stone-900 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Sparkles className="w-10 h-10 mx-auto mb-4 text-stone-400" />
            <h2 className="text-3xl font-medium mb-4">Not sure if this material fits your project?</h2>
            <p className="text-stone-400 mb-8 max-w-xl mx-auto">
              Material Advisors can help evaluate alternatives and identify similar materials 
              based on your specific requirements.
            </p>
            <Button 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-stone-900 px-8"
              onClick={() => navigate('/visual-search')}
            >
              Ask a Material Advisor
            </Button>
          </div>
        </section>
        
        {/* Save to Board CTA */}
        <section className="py-12 bg-stone-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-medium text-stone-900 mb-4">Save this material to your project board</h2>
            {user ? (
              <Button 
                className="bg-stone-900 hover:bg-stone-800 text-white px-8"
                onClick={() => setShowAddToBoard(true)}
              >
                Save to board
              </Button>
            ) : (
              <div>
                <p className="text-stone-500 mb-4">Create a free account to save materials</p>
                <Button 
                  className="bg-stone-900 hover:bg-stone-800 text-white px-8"
                  onClick={() => navigate('/register', { state: { from: `/materials/${materialId}` } })}
                >
                  Create free account
                </Button>
              </div>
            )}
          </div>
        </section>
        
        {/* Related Materials by Application */}
        {material.applications?.length > 0 && similarMaterials.length > 0 && (
          <section className="py-12 bg-white">
            <div className="max-w-7xl mx-auto px-6">
              <h2 className="text-2xl font-medium text-stone-900 mb-2">
                Best materials for {material.applications[0]}
              </h2>
              <p className="text-stone-500 mb-6">Popular choices for this application</p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {similarMaterials.slice(0, 6).map((mat) => (
                  <SimilarMaterialCard key={mat.material_id} material={mat} />
                ))}
              </div>
            </div>
          </section>
        )}
        
        {/* SEO Footer Content */}
        <section className="py-12 bg-stone-100">
          <div className="max-w-4xl mx-auto px-6">
            <p className="text-sm text-stone-600 leading-relaxed">
              {material.name} is a premium {material.stone_type?.toLowerCase()} commonly used in luxury kitchens, 
              bathrooms, and architectural applications due to its durability and elegant appearance. 
              This material features {visualProfile.pattern_type?.toLowerCase()} patterns with {visualProfile.contrast_level?.toLowerCase()} contrast, 
              making it suitable for {material.applications?.join(', ') || 'various applications'}. 
              Similar materials include other {material.stone_type?.toLowerCase()} varieties with comparable visual characteristics.
            </p>
          </div>
        </section>
      </main>
      
      <Footer />
      
      {/* Create Board Dialog */}
      <Dialog open={showCreateBoard} onOpenChange={setShowCreateBoard}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">Create New Project</DialogTitle>
          </DialogHeader>
          <form onSubmit={createBoardAndAdd} className="space-y-4 mt-4">
            <p className="text-sm text-stone-500">
              Creating project and adding <strong>{material?.name}</strong>
            </p>
            <div>
              <Label className="text-sm mb-2 block">Project Name *</Label>
              <Input
                value={newBoardForm.name}
                onChange={(e) => setNewBoardForm({...newBoardForm, name: e.target.value})}
                placeholder="e.g., Kitchen Countertops"
                required
              />
            </div>
            <div>
              <Label className="text-sm mb-2 block">Client / Reference</Label>
              <Input
                value={newBoardForm.project_name}
                onChange={(e) => setNewBoardForm({...newBoardForm, project_name: e.target.value})}
                placeholder="e.g., Smith Residence"
              />
            </div>
            <div>
              <Label className="text-sm mb-2 block">
                <MapPin className="w-3 h-3 inline mr-1" />
                Project City
              </Label>
              <Popover open={cityOpen} onOpenChange={setCityOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between font-normal"
                  >
                    {newBoardForm.city || "Select city..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput 
                      placeholder="Search cities..." 
                      value={citySearch}
                      onValueChange={setCitySearch}
                    />
                    <CommandList>
                      <CommandEmpty>No city found.</CommandEmpty>
                      <CommandGroup>
                        {filteredCities.map((city) => (
                          <CommandItem
                            key={city}
                            value={city}
                            onSelect={() => {
                              setNewBoardForm({...newBoardForm, city});
                              setCityOpen(false);
                              setCitySearch('');
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                newBoardForm.city === city ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {city}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowCreateBoard(false)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-stone-900 hover:bg-stone-800 text-white">
                Create & Add
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Fullscreen Gallery */}
      {showGallery && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" data-testid="gallery-modal">
          <button
            onClick={() => setShowGallery(false)}
            className="absolute top-6 right-6 p-2 text-white hover:bg-white/10 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
          <button
            onClick={() => setGalleryIndex(prev => prev > 0 ? prev - 1 : allImages.length - 1)}
            className="absolute left-6 p-3 text-white hover:bg-white/10 rounded-full"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <img 
            src={allImages[galleryIndex]} 
            alt={material.name}
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
          <button
            onClick={() => setGalleryIndex(prev => prev < allImages.length - 1 ? prev + 1 : 0)}
            className="absolute right-6 p-3 text-white hover:bg-white/10 rounded-full"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          <div className="absolute bottom-6 text-white text-sm">
            {galleryIndex + 1} / {allImages.length}
          </div>
        </div>
      )}
      
      {/* Request Slabs Modal */}
      <RequestSlabsModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        material={material}
        user={user}
      />
    </div>
  );
};

export default MaterialDetail;
